import { zodResolver } from "@hookform/resolvers/zod";
import {
  getLocalTimeZone,
  now,
  toCalendarDateTime,
} from "@internationalized/date";
import { TimedDuration } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/durationInputs/TimedDuration";
import {
  useAddVehicle,
  useGetIncidentNotes,
  useGetOperators,
  useGetTrainStations,
  useUpdateVehicle,
} from "features/VehicleSidebar/VehicleList/AddVehicles/useInfoDataForVehicles";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, FormProvider } from "react-hook-form";
import { ActionModal } from "shared/components/ActionModal";
import {
  Combobox,
  DropsStaticAlert,
  Field,
  Input,
  Item,
  Radio,
  RadioGroup,
  Text,
  VStack,
} from "shared/components/Spor";
import { Select } from "shared/components/forms/Select";
import { useFormVerbose } from "shared/form/useFormVerbose";
import { toFormTime } from "shared/utils/dateTimeUtils";
import {
  AddReserveAlternativeTransportRequest,
  AddReserveAlternativeTransportRequestSchema,
  ReserveAlternativeTransport,
  vehicleTypeSchema,
} from "shared/types/alternativeTransport";
import styled, { css } from "styled-components";
import { StyleTarget } from "shared/components/StyleTarget";

const RadioGroupStyled = styled(RadioGroup)`
  display: flex;
  justify-content: flex-start;
  gap: 24px;
`;

/* Fix tilknyttet stasjon border not aligning with input border and scroll not displaying all items without clipping */
const comboboxPopoverFix = css`
  width: 100%;
  ul[role="listbox"] {
    overscroll-behavior: contain;
  }

  div:has(> ul[role="listbox"]) {
    left: -2px !important;
    width: calc(100% + 4px) !important;
    min-width: calc(100% + 4px) !important;
  }
`;

/**
 * Keeps the scrollable area contained within the reservebuss modal and ensures the modal is scrolled to bottom so the list isn't tiny.
 */
const useFitComboboxToFooter = (wrapperRef: {
  current: HTMLDivElement | null;
}) => {
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    // Nearest scrollable ancestor (the modal's scrollable body).
    const getScrollParent = (el: HTMLElement | null): HTMLElement | null => {
      let node = el?.parentElement ?? null;
      while (node) {
        const { overflowY } = getComputedStyle(node);
        if (
          (overflowY === "auto" || overflowY === "scroll") &&
          node.scrollHeight > node.clientHeight
        ) {
          return node;
        }
        node = node.parentElement;
      }
      return null;
    };

    // Scroll the modal body to the bottom when the input gains focus
    const scrollToBottomOnFocus = () => {
      if (wrapper.querySelector('ul[role="listbox"]')) return;
      getScrollParent(wrapper)?.scrollTo({ top: Number.MAX_SAFE_INTEGER });
    };

    const adjust = () => {
      const listbox = wrapper.querySelector<HTMLElement>('ul[role="listbox"]');
      if (!listbox) {
        return;
      }

      // Cap the height so the list ends above the footer.
      const dialogContent = wrapper.closest<HTMLElement>(
        '[data-scope="dialog"][data-part="content"]',
      );
      const submitButton = dialogContent?.querySelector<HTMLElement>(
        '[data-testid="modal_submitButton"]',
      );
      let footer: HTMLElement | null = submitButton ?? null;
      while (footer && footer.parentElement !== dialogContent) {
        footer = footer.parentElement;
      }
      const boundaryTop = footer
        ? footer.getBoundingClientRect().top
        : window.innerHeight;

      const available = boundaryTop - listbox.getBoundingClientRect().top - 8;
      if (available > 0) {
        listbox.style.maxHeight = `${available}px`;
      }
    };

    const observer = new MutationObserver(adjust);
    observer.observe(wrapper, { childList: true, subtree: true });
    wrapper.addEventListener("focusin", scrollToBottomOnFocus);
    window.addEventListener("resize", adjust);
    window.addEventListener("scroll", adjust, true);
    adjust();

    return () => {
      observer.disconnect();
      wrapper.removeEventListener("focusin", scrollToBottomOnFocus);
      window.removeEventListener("resize", adjust);
      window.removeEventListener("scroll", adjust, true);
    };
  }, [wrapperRef]);
};

type AddVehicleProps = {
  closeModal: () => void;
  isReserveBusTabSelected: boolean;
  editVehicle?: ReserveAlternativeTransport;
};

const AddVehicle = ({
  closeModal,
  isReserveBusTabSelected,
  editVehicle,
}: AddVehicleProps) => {
  const isEdit = !!editVehicle;
  const [searchQuery, setSearchQuery] = useState("");

  const currentTime = now(getLocalTimeZone());
  const nowTime = toCalendarDateTime(currentTime);
  const nowTimePlus30 = toCalendarDateTime(currentTime).add({ minutes: 30 });

  const formMethods = useFormVerbose<AddReserveAlternativeTransportRequest>({
    resolver: zodResolver(AddReserveAlternativeTransportRequestSchema),
    defaultValues: {
      serviceType:
        editVehicle?.serviceType ??
        (isReserveBusTabSelected ? "RESERVE" : "BACKUP"),
      operatorName: editVehicle?.operatorName ?? "",
      operatorId: editVehicle?.operatorId ?? null,
      shiftStartTime: toFormTime(editVehicle?.shiftStartTime) ?? nowTime,
      shiftEndTime: toFormTime(editVehicle?.shiftEndTime) ?? nowTimePlus30,
      iSirkId: editVehicle?.isirkId ?? null,
      referenceNumber: editVehicle?.referenceNumber ?? null,
      driverPhoneNumber: editVehicle?.driverPhoneNumber ?? "",
      vehicleType:
        editVehicle?.type === "BUS" || editVehicle?.type === "MINIBUS"
          ? editVehicle.type
          : undefined,
      baseStopReference: editVehicle?.baseStopReference ?? undefined,
      stopName: editVehicle?.baseStopName ?? "",
    },
  });

  const {
    formState: { errors },
    reset: resetForm,
    register,
    handleSubmit,
    watch,
    setValue,
  } = formMethods;

  const serviceType = watch("serviceType");
  const isReserve = serviceType === "RESERVE";
  const isFromPlan =
    isEdit && (!!editVehicle?.externalSystem || !!editVehicle?.workShiftId);
  const comboboxWrapperRef = useRef<HTMLDivElement>(null);
  useFitComboboxToFooter(comboboxWrapperRef);
  const [isIsirkSelected, setIsIsirkSelected] = useState(
    isEdit ? !!editVehicle?.isirkId : true,
  );
  const [isManualOperator, setIsManualOperator] = useState(
    isEdit ? !editVehicle?.operatorId : false,
  );
  const operatorId = watch("operatorId");
  const [date] = useState(new Date());
  const jbvId = editVehicle?.baseStopReference?.jbvId;

  const {
    data: operationalPointResponse,
    isSuccess: isOpPointResponseSuccess,
    isError: isOpPointResponseError,
  } = useGetTrainStations();

  const {
    mutate: postAddVehicle,
    status: postAddVehicleStatus,
    reset: resetStatus,
  } = useAddVehicle();

  const {
    mutate: postUpdateVehicle,
    status: updateVehicleStatus,
    reset: resetUpdateStatus,
  } = useUpdateVehicle(editVehicle?.id ?? "");

  const mutationStatus = isEdit ? updateVehicleStatus : postAddVehicleStatus;

  const { data: notes, isError: isNotesError } = useGetIncidentNotes(date);

  const { data: operators } = useGetOperators();
  const operatorsList = operators ?? [];

  const currentISirkId = watch("iSirkId");

  const incidentNoteOptions = useMemo<
    { identifier: string; isirk: string }[]
  >(() => {
    const options = (notes?.incidentNotes ?? []).map((note) => ({
      identifier: note.identifier,
      isirk: note.isirk,
    }));

    if (
      currentISirkId &&
      !options.some((option) => option.isirk === currentISirkId)
    ) {
      return [
        { identifier: currentISirkId, isirk: currentISirkId },
        ...options,
      ];
    }

    return options;
  }, [notes, currentISirkId]);

  const onSubmitForm = handleSubmit((data) =>
    isEdit ? postUpdateVehicle(data) : postAddVehicle(data),
  );

  const onClose = () => {
    closeModal();
    resetForm();
    resetStatus();
    resetUpdateStatus();
  };

  const filteredStations = useMemo(() => {
    if (!isOpPointResponseSuccess || !operationalPointResponse?.railStations)
      return [];

    const query = searchQuery.toLowerCase().trim();
    if (!query) return [];

    return operationalPointResponse.railStations
      .filter((station) => station.name.toLowerCase().includes(query))
      .sort((a, b) => {
        const aName = a.name.toLowerCase();
        const bName = b.name.toLowerCase();
        const aStarts = aName.startsWith(query);
        const bStarts = bName.startsWith(query);

        // Prefix matches first, then alphabetical.
        if (aStarts !== bStarts) return aStarts ? -1 : 1;
        return aName.localeCompare(bName, "nb");
      });
  }, [searchQuery, operationalPointResponse, isOpPointResponseSuccess]);

  const onSearchSelectionChange = (selectedStation: string) => {
    const selected = filteredStations.find((s) => s.name === selectedStation);
    if (!selected) return;

    setValue("baseStopReference", {
      nsrCode:
        selected.externalIds.find((e) => e.sourceSystem === "NSR")?.id ?? null,
      jbvId:
        selected.externalIds.find((e) => e.sourceSystem === "TRAINPLAN")?.id ??
        null,
      uicCode: null,
      hastusCode: null,
      nsrStopPlace: null,
      nsrQuay: null,
    });
  };

  useEffect(() => {
    if (!jbvId || !operationalPointResponse) return;

    const matchedStation = operationalPointResponse?.railStations.find(
      (station) =>
        station.externalIds.some(
          (e) => e.sourceSystem === "TRAINPLAN" && e.id === jbvId,
        ),
    );

    if (matchedStation) {
      setSearchQuery(matchedStation.name);
    }
  }, [jbvId, operationalPointResponse]);

  return (
    <FormProvider {...formMethods}>
      <ActionModal
        title={
          isEdit
            ? `Rediger ${isReserve ? "reservebuss" : "beredskapsbuss"}`
            : `Legg til ${isReserve ? "reserve" : "beredskap"}`
        }
        actionTitle={
          isEdit
            ? "Lagre endringer"
            : `Legg til ${isReserve ? "reserve" : "beredskap"}`
        }
        onClose={onClose}
        onSubmit={onSubmitForm}
        successMessage={isEdit ? "Endringer lagret" : "Kjøretøy lagret"}
        failureMessage={
          isEdit ? "Lagring av endringer feilet" : "Lagring av kjøretøy feilet"
        }
        size="lg"
        loading={mutationStatus === "pending"}
        isSuccess={mutationStatus === "success"}
        isError={mutationStatus === "error"}
      >
        {(isOpPointResponseError || isNotesError) && (
          <DropsStaticAlert
            variant="error"
            title="Feil ved henting av nødvendig data"
            style={{ width: "100%" }}
          >
            Det oppstod en feil ved henting av nødvendig data. Det er ikke mulig
            å sette opp busser akkurat nå. Vennligst prøv igjen senere.
          </DropsStaticAlert>
        )}

        <VStack gap="1.2rem" alignItems="start" width="100%">
          <Text fontWeight="700">Velg type buss:</Text>
          <Controller
            name="serviceType"
            control={formMethods.control}
            render={({ field }) => (
              <RadioGroupStyled
                value={field.value}
                onValueChange={(details) => {
                  field.onChange(details.value);
                  if (details.value !== "RESERVE") setValue("iSirkId", null);
                }}
              >
                <Radio value="RESERVE">Reserve</Radio>
                <Radio value="BACKUP">Beredskapsbuss</Radio>
              </RadioGroupStyled>
            )}
          />
          <DropsStaticAlert variant="info" style={{ width: "100%" }}>
            {isReserve
              ? "Denne bussen vil bli synlig for kundeveiledere."
              : "Denne bussen vil kun være synlig for trafikkplanlegger."}
          </DropsStaticAlert>

          <Field
            invalid={!!errors.vehicleType}
            errorText="Kjøretøytype må velges"
            width="100%"
            aria-invalid={!!errors.vehicleType}
            aria-describedby={
              errors.vehicleType ? "vehicleType-error" : undefined
            }
          >
            <Select
              label="Type kjøretøy"
              placeholder="Velg type kjøretøy"
              {...register("vehicleType")}
            >
              <option value={vehicleTypeSchema.Values.BUS}>Buss</option>
              <option value={vehicleTypeSchema.Values.MINIBUS}>Minibuss</option>
            </Select>
          </Field>
          <Text fontWeight="700">Tilgjengelig fra og til</Text>
          <TimedDuration
            fromTimeField="shiftStartTime"
            toTimeField="shiftEndTime"
          />
          <Text fontWeight="700">Leverandør</Text>
          <RadioGroupStyled
            value={isManualOperator ? "manual" : "list"}
            onValueChange={(details) => {
              const manual = details.value === "manual";
              setIsManualOperator(manual);
              setValue("operatorId", null);
              if (!manual) setValue("operatorName", "");
            }}
          >
            <Radio value="list">Velg fra liste</Radio>
            <Radio value="manual">Skriv navn manuelt</Radio>
          </RadioGroupStyled>
          <Field
            invalid={!!errors.operatorName}
            errorText={
              isManualOperator ? "Selskap må fylles ut" : "Selskap må velges"
            }
            width="100%"
            aria-invalid={!!errors.operatorName}
            aria-describedby={
              errors.operatorName ? "operatorName-error" : undefined
            }
          >
            {isManualOperator ? (
              <Input label="Selskap" {...register("operatorName")} />
            ) : (
              <Select
                label="Velg selskap"
                data-testid="operatorSelect"
                value={operatorId ?? ""}
                onChange={(event) => {
                  const selected = operatorsList.find(
                    (operator) => operator.id.toString() === event.target.value,
                  );
                  setValue("operatorName", selected?.name ?? "");
                  setValue("operatorId", selected?.id.toString() ?? null);
                }}
              >
                <option value="">-</option>
                {operatorsList.map((operator) => (
                  <option key={operator.id} value={operator.id.toString()}>
                    {operator.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field
            invalid={!!errors.driverPhoneNumber}
            errorText="Telefonnummer må fylles ut"
            width="100%"
            aria-invalid={!!errors.driverPhoneNumber}
            aria-describedby={
              errors.driverPhoneNumber ? "driverPhoneNumber-error" : undefined
            }
          >
            <Input label="Telefonnummer" {...register("driverPhoneNumber")} />
          </Field>
          <Field
            invalid={!!errors.baseStopReference}
            errorText="Stasjon må velges"
            width="100%"
            aria-invalid={!!errors.baseStopReference}
            aria-describedby={
              errors.baseStopReference ? "baseStopReference-error" : undefined
            }
          >
            <StyleTarget
              ref={comboboxWrapperRef}
              $style={comboboxPopoverFix}
              $target="&&&"
            >
              <Combobox
                label="Tilknyttet stasjon"
                onInputChange={setSearchQuery}
                inputValue={searchQuery}
                onSelectionChange={(key) =>
                  onSearchSelectionChange(key as string)
                }
              >
                {filteredStations.map((station) => (
                  <Item key={station.name} textValue={station.name}>
                    {station.name}
                  </Item>
                ))}
              </Combobox>
            </StyleTarget>
          </Field>
          <Field
            invalid={!!errors.stopName}
            errorText="Stoppnavn må oppgis"
            width="100%"
            aria-invalid={!!errors.stopName}
            aria-describedby={errors.stopName ? "stopName-error" : undefined}
          >
            <Input label="Plassering" {...register("stopName")} />
          </Field>
          {isReserve && (
            <>
              <Text fontWeight="700">Knytt til I-Sirk</Text>
              {isFromPlan && (
                <DropsStaticAlert variant="info" style={{ width: "100%" }}>
                  I-Sirk kan ikke endres når du redigerer et kjøretøy fra plan.
                </DropsStaticAlert>
              )}
              <RadioGroupStyled
                defaultValue={isIsirkSelected ? "isirk" : "referansenummer"}
                onValueChange={(details) => {
                  setIsIsirkSelected(details.value === "isirk");
                  if (details.value !== "isirk") setValue("iSirkId", null);
                }}
                value={isIsirkSelected ? "isirk" : "referansenummer"}
                disabled={isFromPlan}
              >
                <Radio value="isirk">Knytt til I-sirk</Radio>
                <Radio value="referansenummer">Bruk kun referansenummer</Radio>
              </RadioGroupStyled>
            </>
          )}
          {isReserve && isIsirkSelected && (
            <Field width="100%">
              <Select
                label="Velg I-isirk"
                placeholder={
                  incidentNoteOptions.length === 0
                    ? "Ingen i-isirk tilgjengelig denne datoen"
                    : "Velg i-isirk"
                }
                {...register("iSirkId")}
                defaultValue={undefined}
                disabled={isFromPlan}
              >
                {incidentNoteOptions.map((note) => (
                  <option key={note.identifier} value={note.isirk}>
                    {note.isirk}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          <Input label="Referansenummer" {...register("referenceNumber")} />
        </VStack>
      </ActionModal>
    </FormProvider>
  );
};

export default AddVehicle;
